import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Table,
  Checkbox,
  message,
  Card,
  Row,
  Col,
  Input,
  Select,
  Spin,
  Button,
} from "antd";
import { ReloadOutlined, EditOutlined } from "@ant-design/icons";
import { API_URL } from "../../url_config";
import { NavigationStepsCapNoc } from "../../components/navbars/navbar_cap_noc";
import ModifyItemPage from "../../components/modify_page";

const { TextArea } = Input;

// === Configuration des titres de colonnes ===
const columnTitles = {
  ligne: "Ligne",
  coquille: "Coquille à vérifier",
  configuration_ad: "Configuration AD SAE/VE3",
  remontee_de_ad_sae_tr: "Remontée AD SAE-TR",
  geolocalisation: "Géolocalisation AD SAE",
  gestion_ad: "Acquittement / Clôture SAE",
  remontee_de_ad_tetra: "Remontée AD TETRA",
  ecoute_d_ambiance: "Écoute d’ambiance",
  prise_en_compte_appel: "Appel individuel",
  signal: "ÉTAT",
};

// === Style du tableau ===
const tableStyle = document.createElement("style");
tableStyle.innerHTML = `
  .custom-row td {
    border-bottom: 2px solid #d9d9d9 !important;
  }

  .ant-table-thead > tr > th {
    background-color: #f5f5f5 !important;
    font-weight: 600;
    border-bottom: 2px solid #bfbfbf !important;
    color: #333;
  }

  .ant-table-tbody > tr:hover > td {
    background-color: #f0f0f0 !important;
  }

  .ant-table {
    border-collapse: separate !important;
    border-spacing: 0;
  }
`;
document.head.appendChild(tableStyle);

// === Composant interne (zones texte : Commentaire + Aide) ===
function TableGeolocalisation({ record, onChange }) {
  const [comment, setComment] = useState(record.comment || "");
  const [aide, setAide] = useState(record.aide || "");

  useEffect(() => {
    setComment(record.comment || "");
    setAide(record.aide || "");
  }, [record.comment, record.aide]);

  const handleChange = async (field, value) => {
    onChange(record.key, field, value);
    try {
      await axios.patch(
        `${API_URL}/validations/validation_geolocalisation/${record.id}/`,
        { [field]: value }
      );
    } catch (error) {
      console.error(error);
      message.error("Erreur lors de la mise à jour");
    }
  };

  return (
    <div style={{ padding: "20px" }}>
      <Row gutter={16}>
        <Col span={12}>
          <Card title="Commentaire" bordered>
            <TextArea
              rows={5}
              value={comment}
              onChange={(e) => {
                const value = e.target.value;
                setComment(value);
                handleChange("comment", value);
              }}
              placeholder="Saisis un commentaire..."
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card
            title={
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span>Informations</span>
                <span style={{ fontSize: 12, color: "#888" }}>(Supervision)</span>
              </div>
            }
            bordered
          >
            <TextArea
              rows={5}
              value={aide}
              onChange={(e) => {
                const value = e.target.value;
                setAide(value);
                handleChange("aide", value);
              }}
              placeholder="Décris ton besoin d’aide..."
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}

// === Composant principal ===
const ValidationGeolocalisationCapNoc = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // === Chargement des données ===
  const fetchData = async () => {
    setRefreshing(true);
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/validations/validation_geolocalisation/?ot_tag=cap_noc`
      );
      const formatted = res.data.map((item, index) => ({
        key: index,
        ...item,
      }));
      setData(formatted);
    } catch (error) {
      console.error("Erreur de chargement :", error);
      message.error("Erreur de chargement des données");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // === Mise à jour locale + PATCH ===
  const handleDataChange = async (key, field, value) => {
    setData((prev) =>
      prev.map((item) => (item.key === key ? { ...item, [field]: value } : item))
    );

    try {
      const updatedItem = data.find((item) => item.key === key);
      if (updatedItem) {
        await axios.patch(
          `${API_URL}/validations/validation_geolocalisation/${updatedItem.id}/`,
          { [field]: value }
        );
      }
    } catch (error) {
      console.error(error);
      message.error("Erreur lors de la mise à jour");
    }
  };

  // === Colonnes du tableau ===
  const columns = [
    { title: columnTitles.ligne, dataIndex: "ligne" },

    {
      title: columnTitles.configuration_ad,
      dataIndex: "configuration_ad",
      render: (val, record) => (
        <Select
          value={val || ""}
          onChange={(value) => handleDataChange(record.key, "configuration_ad", value)}
          style={{ width: 120, fontWeight: 600 }}
          options={[
            { value: "", label: " " },
            { value: "ok", label: <span style={{ color: "#52c41a" }}>OK</span> },
            { value: "ko", label: <span style={{ color: "#ff4d4f" }}>KO</span> },
          ]}
        />
      ),
    },
    {
      title: columnTitles.coquille,
      dataIndex: "coquille",
      render: (val, record) => (
        <Input
          value={val || ""}
          onChange={(e) => handleDataChange(record.key, "coquille", e.target.value)}
          placeholder="Saisir une coquille..."
          style={{ width: "100%", borderRadius: "6px" }}
        />
      ),
    },
    ...[
      "remontee_de_ad_sae_tr",
      "geolocalisation",
      "gestion_ad",
      // "remontee_de_ad_tetra",
      // "ecoute_d_ambiance",
      // "prise_en_compte_appel",
    ].map((field) => ({
      title: columnTitles[field],
      dataIndex: field,
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) => handleDataChange(record.key, field, e.target.checked)}
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto",
            transform: "scale(1.3)",
            cursor: "pointer",
            accentColor: "#007bff",
          }}
        />
      ),
    })),
    {
      title: columnTitles.signal,
      dataIndex: "signal",
      render: (val, record) => {
        const color = val === 1 ? "#4CAF50" : val === -1 ? "#FF4D4F" : "#BFBFBF";
        const symbol = val === 1 ? "✓" : val === -1 ? "✗" : " ";
        const handleClick = () => {
          const nextVal = val === 0 ? 1 : val === 1 ? -1 : 0;
          handleDataChange(record.key, "signal", nextVal);
        };
        return (
          <div
            onClick={handleClick}
            style={{
              cursor: "pointer",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: "6px",
                backgroundColor: color,
                color: "white",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontWeight: "bold",
                fontSize: "16px",
                transition: "all 0.3s ease",
              }}
            >
              {symbol}
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <Spin
      spinning={loading}
      size="large"
      tip="Chargement..."
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100vh",
        backgroundColor: "rgba(255, 255, 255, 0.8)",
        zIndex: 9999,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div style={{ padding: "20px" }}>
        {/* Menu centré + bouton rafraîchir */}
        <div style={{ position: "relative", marginBottom: 16 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "20px",
            }}
          >
            <NavigationStepsCapNoc selectedKey="geolocalisation" />
          </div>

          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={fetchData}
            loading={refreshing}
            style={{
              backgroundColor: "#007bff",
              borderColor: "#007bff",
              fontWeight: 500,
              position: "absolute",
              top: 0,
              right: 0,
              zIndex: 1000,
            }}
          >
            Rafraîchir
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={data}
          expandable={{
            expandedRowRender: (record) => (
              <TableGeolocalisation
                record={record}
                onChange={handleDataChange}
              />
            ),
          }}
          bordered
          rowClassName={() => "custom-row"}
          pagination={false}
          style={{
            border: "2px solid #bfbfbf",
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          }}
        />

        <div style={{ padding: 20 }}>
          <ModifyItemPage
            apiUrl={`${API_URL}/validations/validation_geolocalisation/`}
            ot_tag="cap_noc"
            title="Gestion"
            rowKey="id"
            trigger={<Button icon={<EditOutlined />} shape="circle" />}
            columns={[{ title: columnTitles.ligne, dataIndex: "ligne", key: "ligne" }]}
            formFields={[
              { name: "ligne", label: columnTitles.ligne, placeholder: "Entrez la ligne" },
            ]}
          />
        </div>
      </div>
    </Spin>
  );
};

export default ValidationGeolocalisationCapNoc;
